// packages/web/src/components/learner/ui.tsx
// Piezas visuales compartidas por las 3 pantallas del alumno (dirección 1b "Patio").
import { ReactNode, useId, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import type { DimLevel } from '@marzano/contracts';
import type { Dictamen, Dim, EvidenceCriteria, Modality } from '../../types';

/** Nombre para niños + color + número. Nunca se indica nivel solo con color. */
export const DIM_KID: Record<Dim, { n: number; kid: string; bar: string; text: string; soft: string }> = {
  recuperacion: { n: 1, kid: 'Recordar', bar: 'bg-dim-rec', text: 'text-dim-rec', soft: 'bg-dim-rec-soft' },
  comprension: { n: 2, kid: 'Explicar', bar: 'bg-dim-com', text: 'text-dim-com', soft: 'bg-dim-com-soft' },
  analisis: { n: 3, kid: 'Comparar', bar: 'bg-dim-ana', text: 'text-dim-ana', soft: 'bg-dim-ana-soft' },
  transferencia: { n: 4, kid: 'Usar lo aprendido', bar: 'bg-dim-tra', text: 'text-dim-tra', soft: 'bg-dim-tra-soft' },
};

/** Mismas 3 bandas de `dimensionLevel` (contracts). Texto en tú, sin voseo. */
export const DIM_LEVEL: Record<DimLevel, { icon: string; label: string; cls: string }> = {
  strong: { icon: '✓', label: 'Lo dominas', cls: 'text-emerald-800' },
  ok: { icon: '◐', label: 'Vas por buen camino', cls: 'text-amber-800' },
  work: { icon: '○', label: 'Para seguir practicando', cls: 'text-slate-700' },
};

/** Estado global del dictamen → palabra para el alumno. Nunca "no evidencia". */
export const STATE_KID: Record<Dictamen['state'], { icon: string; label: string; cls: string }> = {
  no_evidencia: { icon: '○', label: 'Empezando', cls: 'bg-slate-100 text-slate-700' },
  en_desarrollo: { icon: '◐', label: 'En camino', cls: 'bg-amber-100 text-amber-800' },
  suficiente: { icon: '✓', label: '¡Logrado!', cls: 'bg-emerald-100 text-emerald-800' },
  profundo: { icon: '★', label: '¡Lo dominas!', cls: 'bg-marzano-100 text-marzano-800' },
};

export const CRITERIA_KID: Record<EvidenceCriteria, string> = {
  precision: 'Fuiste preciso',
  profundidad: 'Explicaste a fondo',
  independencia: 'Lo hiciste sin ayuda',
  justificacion: 'Dijiste por qué',
};

/** `score` de criterio por pregunta = score_delta 0–25 (scoring.ts lo multiplica ×4). */
export function critStatus(score: number): { icon: string; sr: string; cls: string } {
  const pct = score * 4;
  if (pct >= 75) return { icon: '✓', sr: 'logrado', cls: 'bg-emerald-100 text-emerald-800' };
  if (pct >= 40) return { icon: '◐', sr: 'a medias', cls: 'bg-amber-100 text-amber-800' };
  return { icon: '○', sr: 'aún no', cls: 'bg-slate-100 text-slate-700' };
}

export const MODALITY_KID: Record<Modality, string> = {
  written: 'Escribes',
  oral: 'Hablas',
  mixed: 'Hablas o escribes',
  simplified_1_3: 'Hablas',
};

export function firstName(full: string): string {
  const t = full.trim();
  if (!t) return '';
  const beforeAt = t.includes('@') ? t.split('@')[0]! : t;
  return beforeAt.split(/\s+/)[0] ?? beforeAt;
}

export function GuideAvatar({ size = 48, className = '' }: { size?: number; className?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden className={`shrink-0 ${className}`}>
      <circle cx="24" cy="24" r="24" fill="#00254A" />
      <circle cx="17" cy="22" r="3.2" fill="#fff" />
      <circle cx="31" cy="22" r="3.2" fill="#fff" />
      <path d="M16 31q8 6 16 0" stroke="#fff" strokeWidth="3" fill="none" strokeLinecap="round" />
      <path d="M24 3c6 0 10 4 10 8-6 0-10-3-10-8z" fill="#0F9160" />
    </svg>
  );
}

export function GuideBubble({ children, avatar = 56 }: { children: ReactNode; avatar?: number }) {
  return (
    <div className="flex items-end gap-2.5">
      <GuideAvatar size={avatar} />
      <div className="rounded-[20px] rounded-bl-md bg-white px-4 py-3.5 text-lg leading-snug shadow-sm">{children}</div>
    </div>
  );
}

export function ScoreBar({ value, label, color = 'bg-marzano-600', h = 'h-3' }: { value: number; label: string; color?: string; h?: string }) {
  const v = Math.max(0, Math.min(100, value));
  return (
    <div role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(v)} aria-label={label}
      className={`${h} w-full overflow-hidden rounded-full bg-crema-200`}>
      <div className={`${h} rounded-full ${color}`} style={{ width: `${v}%` }} />
    </div>
  );
}

export function Collapsible({ title, defaultOpen = false, children }: { title: ReactNode; defaultOpen?: boolean; children: ReactNode }) {
  const [open, setOpen] = useState(defaultOpen);
  const id = useId();
  return (
    <div className="flex flex-col gap-3">
      <button type="button" aria-expanded={open} aria-controls={id} onClick={() => setOpen((o) => !o)}
        className="flex min-h-tap items-center justify-between rounded-2xl border-2 border-marzano-600 bg-white px-[18px] text-[17px] font-bold text-marzano-600">
        <span>{title}</span>
        <ChevronDown aria-hidden className={`h-5 w-5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      <div id={id} hidden={!open}>{children}</div>
    </div>
  );
}

export function Wordmark() {
  return (
    <div className="flex flex-col leading-none">
      <span className="text-[9px] tracking-[.3em] text-slate-600">COLEGIO</span>
      <span className="font-serif text-xl tracking-[.04em] text-marzano-600">PIONEROS</span>
    </div>
  );
}

export function SectionTitle({ children }: { children: ReactNode }) {
  return <h2 className="mt-3 text-[22px] font-bold text-marzano-600">{children}</h2>;
}
